import { gql } from '@apollo/client';

import ADDRESS_FRAGMENT from '../fragments/address';
import WORKDAYS from '../fragments/availabilities/workdays';
import CONTACTS_FRAGMENT from '../fragments/contacts';
import GENERAL_FIELDS_FRAGMENT, { generalFields } from '../fragments/general';
import META_FRAGMENT from '../fragments/meta';
import { TRANSACTION_TYPES_WITH_PERSONAL_PRICE_FRAGMENT } from '../fragments/services/transactionTypes';
import USER_FRAGMENT from '../fragments/user';

export const GET_SINGLE_USER = gql`
  ${USER_FRAGMENT}
  query getUser($email: String, $id: String) {
    findOneUserEntity(email: $email, id: $id) {
      ...UserFragment
    }
  }
`;

export const getUsers = gql`
  ${ADDRESS_FRAGMENT}
  ${GENERAL_FIELDS_FRAGMENT}
  ${META_FRAGMENT}
  query getUsersWithRole(
    $pagination: PageOptionsDto
    $status: StatusType
    $role: RoleType
    $isVerified: Boolean
    $order: UserSortFields
    $query: String
    $state: String
    $locationIds: [String!]
  ) {
    findUserEntityWithRole(
      pagination: $pagination
      order: $order
      status: $status
      role: $role
      isVerified: $isVerified
      query: $query
      state: $state
      locationIds: $locationIds
    ) {
      data {
        ...GeneralFieldsFragment
        ...AddressFragment
        inviteUrl
        companyRole
      }
      meta {
        ...MetaFragment
      }
    }
  }
`;

export const GET_TRANSACTION_ASSIGNERS = gql`
  ${GENERAL_FIELDS_FRAGMENT}
  ${META_FRAGMENT}
  query getTransactionAssigners(
    $pagination: PageOptionsDto
    $status: StatusType
    $role: RoleType
    $order: UserSortFields
    $query: String
    $sortAssignedTransaction: String
    $mainLocationIds: [String!]
  ) {
    findUserEntity(
      pagination: $pagination
      order: $order
      status: $status
      role: $role
      query: $query
      sortAssignedTransaction: $sortAssignedTransaction
      mainLocationIds: $mainLocationIds
    ) {
      data {
        ...GeneralFieldsFragment
        firstName
        lastName
        email
        isAssignedTransaction(transaction: $sortAssignedTransaction)
      }
      meta {
        ...MetaFragment
      }
    }
  }
`;

export const GET_NOTARY_FOR_ASSIGNMENT = gql`
  ${WORKDAYS}
  ${CONTACTS_FRAGMENT}
  query findNotaryEntity($appointmentCoordinate: JSON, $matchState: String, $dateTime: String, $query: String, $duration: Float, $requiredScanDoc: Boolean) {
    findNotaryEntity(appointmentCoordinate: $appointmentCoordinate, matchState: $matchState, dateTime: $dateTime, query: $query, duration: $duration, requiredScanDoc: $requiredScanDoc) {
      data {
        isAvailable
        id
        state
        avatar {
          id
          thumbnailPipeFrom
        }
        ${generalFields}
        availability {
          ...Workdays
        }
        contacts {
          ...ContactsFragment
        }
        transactionDistance
      }
    }
  }
`;

export const GET_NOTARY_FOR_TRANSACTION_TYPES_PRICES = gql`
  ${META_FRAGMENT}
  query findServiceTransactionTypeUserEntity(
    $pagination: PageOptionsDto
    $transactionType: String!
    $query: String
    $status: StatusType
  ) {
    findServiceTransactionTypeUserEntity(
      pagination: $pagination
      transactionType: $transactionType
      query: $query
      status: $status
    ) {
      data {
        id
        role
        status
        firstName
        lastName
        avatar {
          id
          thumbnailPipeFrom
        }
        specificPrices(transactionType: $transactionType) {
          priceRON
          priceInPerson
        }
      }
      meta {
        ...MetaFragment
      }
    }
  }
`;

export const GET_USER_SERVICE_TYPE_AND_LOCATIONS = gql`
  ${TRANSACTION_TYPES_WITH_PERSONAL_PRICE_FRAGMENT}
  query findOneUserEntity($id: String, $user: String) {
    findOneUserEntity(id: $id) {
      id
      usersLocations {
        location {
          id
          name
          address
        }
      }
      company {
        serviceTypes {
          id
          name
          description
          transactionTypes {
            ...TransactionTypesWithPersonalPriceFragment
          }
        }
      }
    }
  }
`;

export const getUsersForAccounting = gql`
  ${GENERAL_FIELDS_FRAGMENT}
  ${META_FRAGMENT}
  query getUsers(
    $pagination: PageOptionsDto
    $role: RoleType
    $order: UserSortFields
    $query: String
    $hasBalance: Boolean
  ) {
    findInvoiceUserEntity(pagination: $pagination, order: $order, role: $role, query: $query, hasBalance: $hasBalance) {
      data {
        ...GeneralFieldsFragment
        createdTransactions {
          appointment {
            id
          }
        }
        notaryTransactions {
          appointment {
            id
          }
        }
        invoices {
          total
          balance
          paid
        }
        company {
          name
        }
        total
        paid
        balance
        appointmentCount
      }
      meta {
        ...MetaFragment
      }
    }
  }
`;

export const GET_USER_FOR_ACCOUNTING = gql`
  ${GENERAL_FIELDS_FRAGMENT}
  ${ADDRESS_FRAGMENT}
  query getUser($email: String, $id: String) {
    findOneUserEntity(email: $email, id: $id) {
      ...GeneralFieldsFragment
      ...AddressFragment
      createdTransactions {
        appointment {
          id
        }
      }
      company {
        id
        name
      }
      notaryTransactions {
        appointment {
          id
        }
      }
      # total
      # balance
      # paid
      appointmentCount
    }
  }
`;

export const GET_USERS_IN_LOCATION_FORM = gql`
  query getUsersInLocationForm($pagination: PageOptionsDto, $companyRole: CompanyRoleType, $query: String) {
    findUserEntityWithRole(pagination: $pagination, companyRole: $companyRole, query: $query) {
      data {
        id
        firstName
        lastName
        avatar {
          id
          thumbnailPipeFrom
          pipeFrom
        }
        company {
          locations {
            name
            id
            address
          }
        }
      }
    }
  }
`;

export const GET_NOTARY_USERS_BY_INVOICE = gql`
  ${GENERAL_FIELDS_FRAGMENT}
  ${META_FRAGMENT}
  query getUsers(
    $pagination: PageOptionsDto
    $role: RoleType
    $order: UserSortFields
    $query: String
    $hasBalance: Boolean
    $id: String
  ) {
    findInvoiceUserEntity(
      pagination: $pagination
      order: $order
      role: $role
      query: $query
      hasBalance: $hasBalance
      id: $id
    ) {
      data {
        ...GeneralFieldsFragment
        total
        paid
        balance
        appointmentCount
        address1
        address2
        state
        city
        zip
      }
      meta {
        ...MetaFragment
      }
    }
  }
`;

export const GET_USERS_HAVE_TRANSACTION_ACTIVITY = gql`
  ${META_FRAGMENT}
  query getUserWithTransaction($transactionId: String!, $pagination: PageOptionsDto, $query: String) {
    findActivityUserEntity(transactionId: $transactionId, pagination: $pagination, query: $query) {
      data {
        id
        firstName
        lastName
        avatar {
          id
          thumbnailPipeFrom
        }
      }
      meta {
        ...MetaFragment
      }
    }
  }
`;
